import { safePortalRedirect } from './portalNavigation'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { hasPortalEntryAccess } from './portalAccess'
import { isPortalPreviewActive, portalPreviewState } from './portalPreview'

const routes: RouteRecordRaw[] = [
  {
    path: '/admin-preview',
    name: 'portal-preview',
    component: () => import('./PortalPreviewEntryView.vue'),
    meta: { title: 'Просмотр кабинета', public: true, skipAuthInitialization: true },
  },
  {
    path: '/:participantType(student|teacher)/:code/materials/:lessonId?',
    name: 'public-classroom-materials',
    component: () => import('./ClassroomMaterialsView.vue'),
    props: true,
    meta: { title: 'Материалы', public: true, skipAuthInitialization: true },
  },
  {
    path: '/join/:participantType(student|teacher)/:code/materials/:lessonId?',
    redirect: to => ({ path: to.path.replace(/^\/join/i, ''), query: to.query, hash: to.hash }),
  },
  {
    path: '/pay/:token',
    name: 'portal-payment',
    component: () => import('./PortalPaymentView.vue'),
    meta: { title: 'Оплата занятий', public: true },
  },
  {
    path: '/student/:code',
    name: 'public-student-classroom-access',
    component: () => import('./PublicClassroomAccessView.vue'),
    props: route => ({ participantType: 'student', code: route.params.code }),
    meta: { title: 'Уроки ученика', public: true, skipAuthInitialization: true },
  },
  {
    path: '/student/:code/lessons/:lessonId',
    name: 'public-student-lesson-access',
    component: () => import('./PublicClassroomAccessView.vue'),
    props: route => ({ participantType: 'student', code: route.params.code, lessonId: route.params.lessonId }),
    meta: { title: 'Онлайн-урок', public: true, skipAuthInitialization: true },
  },
  {
    path: '/teacher/:code',
    name: 'public-teacher-classroom-access',
    component: () => import('./PublicClassroomAccessView.vue'),
    props: route => ({ participantType: 'teacher', code: route.params.code }),
    meta: { title: 'Уроки преподавателя', public: true, skipAuthInitialization: true },
  },
  {
    path: '/teacher/:code/lessons/:lessonId',
    name: 'public-teacher-lesson-access',
    component: () => import('./PublicClassroomAccessView.vue'),
    props: route => ({ participantType: 'teacher', code: route.params.code, lessonId: route.params.lessonId }),
    meta: { title: 'Онлайн-урок', public: true, skipAuthInitialization: true },
  },
  {
    path: '/join/:participantType(student|teacher)/:code',
    redirect: to => ({ path: to.path.replace(/^\/join/i, ''), query: to.query, hash: to.hash }),
  },
  {
    path: '/join/:participantType(student|teacher)/:code/lessons/:lessonId',
    redirect: to => ({ path: to.path.replace(/^\/join/i, ''), query: to.query, hash: to.hash }),
  },
  {
    path: '/ui-kit',
    name: 'portal-ui-kit',
    component: () => import('@/views/ui-kit/UiKitView.vue'),
    props: { context: 'portal' },
    meta: { title: 'Interface system', public: true, skipAuthInitialization: true },
  },
  {
    path: '/login',
    name: 'portal-login',
    component: () => import('./PortalLoginView.vue'),
    meta: { title: 'Вход', public: true },
  },
  {
    path: '/activate',
    name: 'portal-activate',
    component: () => import('./PortalActivateView.vue'),
    meta: { title: 'Активация', public: true },
  },
  {
    path: '/forbidden',
    name: 'portal-forbidden',
    component: () => import('./PortalAccessDeniedView.vue'),
    meta: { title: 'Нет доступа', allowNonPortal: true },
  },
  {
    path: '/',
    component: () => import('./PortalShell.vue'),
    meta: { title: 'Личный кабинет' },
    children: [
      {
        path: '',
        name: 'portal-home',
        component: () => import('./PortalHomeView.vue'),
        meta: { title: 'Личный кабинет' },
      },
      {
        path: 'lessons',
        name: 'portal-lessons',
        component: () => import('./PortalLessonsView.vue'),
        meta: { title: 'Уроки' },
      },
      {
        path: 'lessons/:lessonId/materials',
        name: 'portal-materials',
        component: () => import('./ClassroomMaterialsView.vue'),
        props: true,
        meta: { title: 'Материалы урока' },
      },
      {
        path: 'materials',
        redirect: to => ({ name: 'portal-lessons', query: to.query, hash: to.hash }),
      },
      {
        path: 'materials/:lessonId',
        redirect: to => ({ name: 'portal-materials', params: { lessonId: to.params.lessonId }, query: to.query, hash: to.hash }),
      },
      {
        path: 'homework',
        name: 'portal-homework',
        component: () => import('./PortalHomeworkView.vue'),
        meta: { title: 'Домашние задания' },
      },
      {
        path: 'reviews',
        name: 'portal-reviews',
        component: () => import('./PortalReviewsView.vue'),
        meta: { title: 'Отзывы' },
      },
      {
        path: 'messages',
        name: 'portal-messages',
        component: () => import('./PortalMessagesView.vue'),
        meta: { title: 'Чаты' },
      },
      {
        path: 'billing',
        name: 'portal-billing',
        component: () => import('./PortalBillingView.vue'),
        meta: { title: 'Оплаты', permission: 'portal.pay' },
      },
      {
        path: 'billing/offers/:id',
        name: 'portal-offer',
        component: () => import('./PortalPurchaseView.vue'),
        meta: { title: 'Выбор пакета', permission: 'portal.pay' },
      },
      {
        path: 'billing/subscriptions/:id',
        name: 'portal-purchase',
        component: () => import('./PortalPurchaseView.vue'),
        meta: { title: 'Покупка занятий', permission: 'portal.pay' },
      },
      {
        path: 'payroll',
        name: 'portal-payroll',
        component: () => import('./PortalPayrollView.vue'),
        meta: { title: 'Начисления' },
      },
      {
        path: 'settings',
        name: 'portal-settings',
        component: () => import('./PortalSettingsView.vue'),
        meta: { title: 'Настройки' },
      },
    ],
  },
  {
    path: '/:prefix/:initialCode([a-z]+-[a-z]+-[0-9]{4})',
    alias: '/go/:prefix/:initialCode',
    name: 'short-link-prefixed',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/open/:initialCode?',
    alias: '/go/:initialCode?',
    name: 'short-link-entry',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/:initialCode([a-z]+-[a-z]+-[0-9]{4})',
    name: 'short-link-code',
    component: () => import('@/views/ShortLinkEntryView.vue'),
    props: true,
    meta: { title: 'Открыть ссылку', public: true, skipAuthInitialization: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'portal-home' },
  },
]

export const portalRouter = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})


portalRouter.beforeEach(async to => {
  const auth = useAuthStore()
  if (isPortalPreviewActive() && to.name !== 'portal-preview') {
    if (portalPreviewState.phase.value !== 'ready') return { name: 'portal-preview' }
    // Public bearer-link flows can leave the scoped API and are not part of a preview.
    if (to.meta.public) return { name: 'portal-home' }
  }

  if (to.meta.public) {
    if (to.meta.skipAuthInitialization) return true
    try { await auth.initialize() } catch (error) { if (to.name !== 'portal-payment') throw error }
    if (to.name === 'portal-login' && auth.authenticated && hasPortalEntryAccess(auth.session)) {
      return safePortalRedirect(to.query.redirect)
    }
    return true
  }

  await auth.initialize()

  if (!auth.authenticated) {
    if (isPortalPreviewActive()) return { name: 'portal-preview' }
    return { name: 'portal-login', query: { redirect: to.fullPath } }
  }

  if (!hasPortalEntryAccess(auth.session) && !to.meta.allowNonPortal) {
    return { name: 'portal-forbidden', query: { redirect: to.fullPath } }
  }

  const required = to.meta.permission
  if (required && !auth.hasPermission(required)) return { name: 'portal-forbidden', query: { redirect: to.fullPath } }
  return true
})

if (typeof window !== 'undefined') {
  window.addEventListener('avoschool:auth-required', () => {
    const auth = useAuthStore()
    auth.clear()
    if (isPortalPreviewActive()) {
      void portalRouter.replace({ name: 'portal-preview' })
      return
    }
    const current = portalRouter.currentRoute.value
    if (!current.meta.public) {
      void portalRouter.replace({ name: 'portal-login', query: { redirect: current.fullPath } })
    }
  })
}

portalRouter.afterEach(to => {
  document.title = to.meta.title ? `${String(to.meta.title)} · Avoschool` : 'Личный кабинет · Avoschool'
})

declare module 'vue-router' {
  interface RouteMeta {
    allowNonPortal?: boolean
    skipAuthInitialization?: boolean
  }
}
